import { patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  extraWorkedTimeRequestEntitySchema,
  type ExtraWorkedTimeRequestsResponse,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const patchExtraWorkedTimeRequestMetadataBodySchema = z.object({
  metadata: z.object({
    isNotificationDismissed: z.boolean().optional(),
    isOutcomeAcknowledged: z.boolean().optional(),
  }),
});

const patchExtraWorkedTimeRequestMetadataResponseSchema = z.object({
  data: extraWorkedTimeRequestEntitySchema.extend({
    meta: z.object({
      isNotificationDismissed: z.boolean().optional(),
      isOutcomeAcknowledged: z.boolean().optional(),
    }),
  }),
});

type PatchExtraWorkedTimeRequestMetadataBody = z.infer<
  typeof patchExtraWorkedTimeRequestMetadataBodySchema
>;

export interface PatchExtraWorkedTimeRequestMetadataRequest extends PatchExtraWorkedTimeRequestMetadataBody {
  extraWorkedTimeRequestId: string;
}

export type PatchExtraWorkedTimeRequestMetadataResponse = z.infer<
  typeof patchExtraWorkedTimeRequestMetadataResponseSchema
>;

const EXTRA_WORKED_TIME_REQUESTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`;

export function usePatchExtraWorkedTimeRequestMetadata(
  options: UseMutationOptions<
    PatchExtraWorkedTimeRequestMetadataResponse,
    AxiosError,
    PatchExtraWorkedTimeRequestMetadataRequest
  > = {}
): UseMutationResult<
  PatchExtraWorkedTimeRequestMetadataResponse,
  AxiosError,
  PatchExtraWorkedTimeRequestMetadataRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      extraWorkedTimeRequestId,
      ...data
    }: PatchExtraWorkedTimeRequestMetadataRequest) => {
      const response = await patch({
        url: `${EXTRA_WORKED_TIME_REQUESTS_URL}/${extraWorkedTimeRequestId}`,
        data,
        requestSchema: patchExtraWorkedTimeRequestMetadataBodySchema,
        responseSchema: patchExtraWorkedTimeRequestMetadataResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PATCH_EXTRA_WORKED_TIME_REQUEST_METADATA_FAILURE,
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData<ExtraWorkedTimeRequestsResponse>(
        { queryKey: [EXTRA_WORKED_TIME_REQUESTS_URL] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const index = oldData.data.findIndex((entity) => entity.id === data.data.id);
          if (index === -1) {
            return oldData;
          }

          const updatedEntity = {
            ...data.data,
            // NOTE: patch response sends fewer meta data than the cached response values
            meta: { ...oldData.data[index].meta, ...data.data.meta },
          };
          const updatedData = oldData.data.toSpliced(index, 1, updatedEntity);
          return { ...oldData, data: updatedData };
        }
      );
      options.onSuccess?.(data, variables, context);
    },
  });
}
